import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from '@/components/catalyst-ui/dialog'
import { Button } from '@/components/catalyst-ui/button'
import { Radio, RadioGroup, RadioField } from '@/components/catalyst-ui/radio'
import { Label } from '@/components/catalyst-ui/fieldset'
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid'

export default function WhoisPrivacySettingsDialog({
  open,
  onClose,
  domainName,
  privacySetting,
  onPrivacySettingChange,
  renewalPrice,
  onSave,
  isLoading = false,
}) {
  return (
    <Dialog open={open} onClose={onClose} size="xl" className="!rounded-xl p-5 !bg-[#FAFAFA]">
      <div className="flex items-center justify-between">
        <DialogTitle className="!text-lg !font-semibold !text-[#09090B]">WHOIS Privacy Settings</DialogTitle>
        <button
          onClick={onClose}
          className="text-black hover:text-zinc-600 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded-full"
        >
          <XMarkIcon className="w-5 h-5 -mt-6 -mr-2.5" />
        </button>
      </div>
      <DialogBody className="!mt-2">
        <DialogDescription className="!text-sm !font-medium !text-[#71717A]">
          Configure privacy protection for {domainName}
        </DialogDescription>
        <div className="mt-4">
          <RadioGroup value={privacySetting} onChange={onPrivacySettingChange}>
            <div className="space-y-3">
              <div className="relative">
                <RadioField className="items-start">
                  <Radio value="private" className="mt-1" />
                  <div className="flex-1">
                    <Label className="text-sm !font-medium text-[#09090B]">Enable WHOIS Privacy</Label>
                    <p className="text-sm !font-medium text-[#71717A] mt-1">
                      Your contact information will be hidden from public WHOIS records. 
                    </p>
                    <p className="text-sm text-[#71717A]"> Cost: $3.00/year</p>
                    {privacySetting === 'private' && renewalPrice && (
                      <div className="py-2 mt-2 flex flex-col gap-1.5 bg-gray-50 rounded text-sm">
                        <div className="flex justify-between mb-1 text-[#71717A] font-medium">
                          <span>Domain renewal:</span>
                          <span className="text-[#09090B]">{typeof renewalPrice === 'string' ? renewalPrice : `$${renewalPrice.toFixed(2)}`}</span>
                        </div>
                        <div className="flex justify-between mb-1 text-[#71717A] font-medium">
                          <span>WHOIS privacy:</span>
                          <span className="text-[#09090B]">$3.00</span>
                        </div>
                        <div className="flex justify-between font-medium pt-1 border-t border-[#E4E4E7] text-[#71717A]">
                          <span>Total renewal cost:</span>
                          <span className="text-[#09090B]">
                            {(() => {
                              let basePrice = 0;
                              if (typeof renewalPrice === 'string') {
                                const match = renewalPrice.match(/[\d.]+/);
                                if (match) {
                                  basePrice = parseFloat(match[0]);
                                }
                              } else if (typeof renewalPrice === 'number') {
                                basePrice = renewalPrice;
                              }
                              
                              if (basePrice > 0) {
                                return `$${(basePrice + 3.00).toFixed(2)}`;
                              }
                              return <span className="text-[#09090B]">{typeof renewalPrice === 'string' ? renewalPrice : `$${renewalPrice.toFixed(2)}`}</span>;
                            })()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </RadioField>
              </div>
              
              <div className="relative">
                <RadioField className="items-start">
                  <Radio value="public" className="mt-1" />
                  <div className="flex-1">
                    <Label className="text-sm !font-medium text-[#09090B]">Disable WHOIS Privacy</Label>
                    <p className="text-sm !font-medium text-[#71717A] mt-1">
                      Your contact information will be publicly visible in WHOIS records.
                    </p>
                    {privacySetting === 'public' && (
                      <p className="text-xs font-medium text-[#A1A1AA] mt-1">
                        Note: If you disable WHOIS privacy mid-term, the service continues until the next renewal. 
                        No refunds are issued for mid-term changes.
                      </p>
                    )}
                  </div>
                </RadioField>
              </div>
            </div>
          </RadioGroup>

          <div className="rounded-md mt-4 border-[1.5px] border-[#3B82F6] bg-[#F1F7FF] p-4">
            <div className="flex items-start gap-3">
              <InformationCircleIcon className="w-5 h-5 text-[#1D4ED8] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-[#1D4ED8]">
                  About WHOIS Privacy
                </div>
                <div className="mt-1 text-sm font-medium text-[#1D4ED8] pr-8">
                  WHOIS privacy hides your personal contact information from public WHOIS databases. 
                  When enabled, it costs <strong>$3.00 per year</strong> and is automatically renewed alongside your domain renewal.
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogBody>
      <DialogActions className="!mt-4 !flex !flex-col sm:!flex-row !gap-3">
        <Button 
          onClick={onClose}
          outline
          className="w-full sm:flex-1 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
        >
          Cancel
        </Button>
        <Button 
          onClick={onSave}
          color="dark"
          disabled={isLoading}
          className="w-full sm:flex-1 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
